import { useEffect, useState } from "react";
import axios from "axios";
import { useForm, Controller, useWatch } from "react-hook-form";
import { FormControlLabel, InputAdornment, Switch, TextField, ToggleButton, ToggleButtonGroup, Tooltip } from "@mui/material";
import { NumericFormat } from "react-number-format";
import { usePlacesWidget } from "react-google-autocomplete";
import { Navigation, Package, Save, Tag, Truck } from "react-feather";
import { authData } from "../../components/GetAuth";
import { calculateCharge, gbp } from "../../../utils/bookingCharges";
import styles from "./companyprofile.module.css";

const SERVICES = [
  { key: "collection", title: "Collection charges", subtitle: "Charged when a driver collects goods from the customer.", icon: <Package />, countryKey: "pickup_country", via: "pickup address" },
  { key: "delivery", title: "Delivery charges", subtitle: "Charged when goods are delivered to the recipient.", icon: <Truck />, countryKey: "delivery_country", via: "recipient address" },
];

const emptyPoint = { address: "", latitude: "", longitude: "", post_code: "" };

const emptyCharge = {
  enabled: false,
  type: "fixed",
  fixed_amount: "",
  rate_per_mile: "",
  base_miles: "",
  base_charge: "",
  minimum_charge: "",
  same_end_point: true,
  start_point: emptyPoint,
  end_point: emptyPoint,
};

const toFormCharge = (charge) => {
  const value = { ...emptyCharge, ...(charge || {}) };
  Object.keys(value).forEach((k) => {
    if (value[k] === null) value[k] = "";
  });
  value.enabled = !!value.enabled;
  value.same_end_point = value.same_end_point === "" || value.same_end_point === undefined ? true : !!value.same_end_point;
  value.start_point = { ...emptyPoint, ...(charge?.start_point || {}) };
  value.end_point = value.same_end_point ? emptyPoint : { ...emptyPoint, ...(charge?.end_point || {}) };
  return value;
};

// Address picked from Google suggestions, restricted to the service's country.
// Typing without picking a suggestion clears the coordinates, so the point stays invalid until one is picked.
const PointInput = ({ value, onChange, onBlur, label, country, error, hint }) => {
  const [text, setText] = useState(value?.address || "");

  useEffect(() => {
    if (value?.address) setText(value.address);
  }, [value?.address]);

  const { ref } = usePlacesWidget({
    apiKey: process.env.REACT_APP_MAP_API_KEY,
    onPlaceSelected: (place) => {
      if (!place?.geometry) return;
      let postCode = "";
      place?.address_components?.forEach((item) => {
        if (item.types.indexOf("postal_code") > -1) postCode = item.long_name;
      });
      setText(place.formatted_address);
      onChange({
        address: place.formatted_address,
        latitude: place.geometry.location.lat(),
        longitude: place.geometry.location.lng(),
        post_code: postCode,
      });
    },
    options: { types: ["geocode"], ...(country ? { componentRestrictions: { country } } : {}) },
  });

  return (<div>
    <TextField
      inputRef={ref}
      label={label}
      variant="outlined"
      fullWidth
      value={text}
      onBlur={onBlur}
      onChange={(e) => {
        setText(e.target.value);
        if (value?.address) onChange(emptyPoint);
      }}
      error={!!error}
      placeholder="Start typing and pick an address"
    />
    {error
      ? <span className={`${styles.FieldError}`}>{error.message}</span>
      : hint && <span className={`${styles.FieldHint}`}>{hint}</span>}
  </div>);
};

const PointField = ({ control, name, label, country, hint, isRequired }) => (
  <Controller
    name={name}
    control={control}
    rules={{ validate: (v, values) => !isRequired(values) || (!!v?.address && v?.latitude !== "" && v?.latitude !== null) || `${label.replace("*", "")} is required. Pick an address from the suggestions.` }}
    render={({ field, fieldState }) => (
      <PointInput value={field.value} onChange={field.onChange} onBlur={field.onBlur} label={label} country={country} error={fieldState.error} hint={hint} />
    )}
  />
);

const MoneyField = ({ control, name, label, requiredType, suffix, errors, hint }) => {
  const serviceKey = name.split(".")[0];
  const error = name.split(".").reduce((o, k) => o?.[k], errors);
  return (<div>
    <Controller
      name={name}
      control={control}
      // Only required while the charge is on and this field belongs to the selected type
      rules={{ validate: (v, values) => !requiredType || !values?.[serviceKey]?.enabled || values?.[serviceKey]?.type !== requiredType || v !== "" || `${label.replace("*", "")} is required` }}
      render={({ field: { onChange, ...field } }) => (
        <NumericFormat
          {...field}
          onValueChange={(v) => onChange(v.value)}
          customInput={TextField}
          label={label}
          variant="outlined"
          fullWidth
          allowNegative={false}
          decimalScale={2}
          inputMode="decimal"
          error={!!error}
          InputProps={suffix === "mi"
            ? { endAdornment: <InputAdornment position="end">miles</InputAdornment> }
            : { startAdornment: <InputAdornment position="start">£</InputAdornment>, ...(suffix ? { endAdornment: <InputAdornment position="end">{suffix}</InputAdornment> } : {}) }}
        />
      )}
    />
    {error
      ? <span className={`${styles.FieldError}`}>{error.message}</span>
      : hint && <span className={`${styles.FieldHint}`}>{hint}</span>}
  </div>);
};

const ChargeCard = ({ service, control, errors, country }) => {
  const charge = useWatch({ control, name: service.key });
  const [miles, setMiles] = useState("10");
  const { total, breakdown } = calculateCharge(charge, miles);
  const name = (field) => `${service.key}.${field}`;
  const perMileOn = (values) => !!values?.[service.key]?.enabled && values?.[service.key]?.type === "mile";

  return (<section className={`${styles.Card}`}>
    <div className={`${styles.CardHead}`}>
      <div>
        <h2 className={`${styles.CardTitle}`}>{service.icon}{service.title}</h2>
        <p className={`${styles.CardSubtitle}`}>{service.subtitle}</p>
      </div>
      <div className={`${styles.ChargeHeadRight}`}>
        <span className={`${styles.StatusPill} ${charge?.enabled ? styles.StatusOn : styles.StatusOff}`}>{charge?.enabled ? "On" : "Off"}</span>
        <Controller
          name={name("enabled")}
          control={control}
          render={({ field }) => (
            <Tooltip title={field.value ? "Turn off" : "Turn on"}>
              <Switch checked={!!field.value} onChange={(e) => field.onChange(e.target.checked)} color="error" inputProps={{ "aria-label": `Enable ${service.title.toLowerCase()}` }} />
            </Tooltip>
          )}
        />
      </div>
    </div>

    <div className={`${styles.CardBody} ${charge?.enabled ? "" : styles.Disabled}`}>
      <Controller
        name={name("type")}
        control={control}
        render={({ field }) => (
          <ToggleButtonGroup exclusive value={field.value} onChange={(e, v) => v && field.onChange(v)} className={`${styles.TypeToggle}`} aria-label="Charge type">
            <ToggleButton value="fixed"><Tag /> Fixed</ToggleButton>
            <ToggleButton value="mile"><Navigation /> Per mile</ToggleButton>
          </ToggleButtonGroup>
        )}
      />

      {charge?.type === "fixed" ? (
        <div className={`${styles.FormGrid}`}>
          <div className={`${styles.FormFull}`}>
            <MoneyField control={control} errors={errors} name={name("fixed_amount")} label="Flat rate*" requiredType="fixed" hint="Charged once per booking." />
          </div>
        </div>
      ) : (
        <div className={`${styles.FormGrid}`}>
          <MoneyField control={control} errors={errors} name={name("rate_per_mile")} label="Rate per mile*" suffix="/ mi" requiredType="mile" />
          <MoneyField control={control} errors={errors} name={name("minimum_charge")} label="Minimum charge" hint="Optional. Lowest amount ever charged." />
          <MoneyField control={control} errors={errors} name={name("base_charge")} label="Base charge" hint="Optional. Charged on every booking." />
          <MoneyField control={control} errors={errors} name={name("base_miles")} label="Miles included in base" suffix="mi" hint="Optional. Per-mile rate starts after these." />

          <p className={`${styles.GroupLabel}`}>Route</p>
          <div className={`${styles.FormFull}`}>
            <PointField control={control} name={name("start_point")} label="Start point*" country={country} isRequired={perMileOn}
              hint={`Distance is measured from here to the ${service.via}${country ? ` (${country} addresses only)` : ""}.`} />
          </div>
          <div className={`${styles.FormFull}`}>
            <Controller
              name={name("same_end_point")}
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={<Switch checked={!!field.value} onChange={(e) => field.onChange(e.target.checked)} color="error" />}
                  label="End point is the same as start point"
                />
              )}
            />
          </div>
          {!charge?.same_end_point && (
            <div className={`${styles.FormFull}`}>
              <PointField control={control} name={name("end_point")} label="End point*" country={country}
                isRequired={(values) => perMileOn(values) && !values?.[service.key]?.same_end_point}
                hint={`Where the driver finishes after the ${service.via}.`} />
            </div>
          )}
        </div>
      )}

      <div className={`${styles.Estimator}`}>
        <div className={`${styles.EstimatorHead}`}>
          <span>Example:</span>
          {charge?.type === "mile" && (<>
            <TextField size="small" className={`${styles.EstimatorInput}`} value={miles} onChange={(e) => setMiles(e.target.value.replace(/[^\d.]/g, ""))} inputProps={{ inputMode: "decimal", "aria-label": "Example distance in miles" }} />
            <span>miles</span>
          </>)}
          <span className={`${styles.EstimatorTotal}`}>{gbp(total)}</span>
        </div>
        <p className={`${styles.EstimatorBreakdown}`}>{breakdown}</p>
        {charge?.type === "mile" && (
          <p className={`${styles.EstimatorBreakdown}`}>Miles = start point → {service.via} → {charge?.same_end_point ? "back to start point" : "end point"}.</p>
        )}
      </div>
    </div>
  </section>);
};

const ChargeSettings = ({ charges, onSaved, showMessage }) => {
  const auth = authData();
  const API_TOKEN = auth?.api_token;
  const [saving, setSaving] = useState(false);

  const { handleSubmit, control, reset, formState: { errors } } = useForm({
    mode: "onChange",
    defaultValues: { collection: toFormCharge(null), delivery: toFormCharge(null) },
  });

  useEffect(() => {
    reset({ collection: toFormCharge(charges?.collection), delivery: toFormCharge(charges?.delivery) });
  }, [charges, reset]);

  const onSubmit = (data) => {
    setSaving(true);
    axios.post(process.env.REACT_APP_API_URL + "v1/company-settings/charges", data, {
      headers: { Authorization: `Bearer ${API_TOKEN}` }
    }).then((response) => {
      setSaving(false);
      if (response.data.status === "SUCCESS") {
        showMessage(response.data.message, "success");
        onSaved(response.data.data);
      } else {
        showMessage(response.data.message, "error");
      }
    }).catch(() => {
      setSaving(false);
      showMessage("Could not save charges. Please try again.", "error");
    });
  };

  return (<form onSubmit={handleSubmit(onSubmit)} noValidate>
    <div className={`${styles.Grid}`}>
      {SERVICES.map((service) => (<ChargeCard key={service.key} service={service} control={control} errors={errors} country={auth?.company?.[service.countryKey]} />))}
    </div>
    <div className={`${styles.CardFooter}`} style={{ borderTop: "none", padding: "18px 0 0" }}>
      <button type="submit" className={`${styles.ActionBU} ${styles.ActionPrimary}`} disabled={saving}><Save /> {saving ? "Saving..." : "Save Charges"}</button>
    </div>
  </form>);
};

export default ChargeSettings;
